
import React from 'react';
import { motion } from 'framer-motion';
import { scrollToElement } from '../utils/transitions';
import { useTranslation } from 'react-i18next';
import { useSettingsStore } from '../store/useSettingsStore';
import { 
  Mail, MapPin, Github, Linkedin, 
  ChevronUp, Heart, Coffee, Code, Cpu 
} from 'lucide-react';

const Footer: React.FC = () => {
  const { t } = useTranslation();
  const { theme } = useSettingsStore();
  const handleNavClick = (id: string) => {
    scrollToElement(id, 80);
  };
  
  const currentYear = new Date().getFullYear();
  
  const socialLinks = [
    { icon: <Github className="w-5 h-5" />, url: "https://github.com/NassimEH", label: "GitHub" },
    { icon: <Linkedin className="w-5 h-5" />, url: "https://www.linkedin.com/in/nassim-elhaddad/", label: "LinkedIn" },
    { icon: <Mail className="w-5 h-5" />, url: "https://nassimelh-blog.vercel.app/", label: "Blog" }
  ];

  return (
    <footer className="relative overflow-hidden">
      {/* Background synthwave - improved for light mode */}
      <div className={`absolute inset-0 pointer-events-none ${
        theme === 'light' 
          ? 'bg-gradient-to-t from-white via-gray-50/95 to-gray-100/90' 
          : 'bg-gradient-to-t from-background via-background/95 to-background/90'
      }`}></div>
      <div className={`absolute inset-0 bg-grid-pattern pointer-events-none ${
        theme === 'light' ? 'opacity-[0.08]' : 'opacity-[0.03]'
      }`}></div>
      
      {/* Glow effect - adjusted for light mode */}
      <div className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-3/4 h-1 blur-xl ${
        theme === 'light' 
          ? 'bg-gradient-to-r from-purple-600/0 via-purple-600/30 to-purple-600/0' 
          : 'bg-gradient-to-r from-purple-500/0 via-purple-500/50 to-purple-500/0'
      }`}></div>
      <div className={`absolute top-0 w-full h-20 bg-gradient-to-b to-transparent pointer-events-none ${
        theme === 'light' ? 'from-purple-600/5' : 'from-purple-600/10'
      }`}></div>
      
      {/* Content */}
      <div className="max-w-7xl mx-auto pt-16 pb-8 px-6 relative z-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-16">
          {socialLinks.map((link, index) => (
            <motion.a
              key={index}
              href={link.url}
              target="_blank"
              rel="noopener noreferrer"
              className={`flex flex-col items-center justify-center glass-morphism p-6 rounded-xl group transition-all duration-300 ${
                theme === 'light' 
                  ? 'border-blue-600/30 bg-white/90 hover:bg-white/95 hover:border-blue-600/50' 
                  : 'border-blue-500/20 hover:border-blue-500/50'
              }`}
              whileHover={{ 
                y: -10, 
                boxShadow: theme === 'light' 
                  ? "0 10px 25px rgba(59,130,246,0.15)" 
                  : "0 0 30px rgba(59,130,246,0.3)"
              }}
            >
              <motion.div 
                className={`mb-3 transition-transform ${
                  theme === 'light' ? 'text-blue-600' : 'text-blue-400'
                }`}
                animate={{ y: [0, -5, 0] }}
                transition={{ 
                  repeat: Infinity, 
                  duration: 2,
                  delay: index * 0.2
                }}
              >
                {link.icon}
              </motion.div>
              <span className={`text-lg font-medium transition-colors ${
                theme === 'light' 
                  ? 'text-gray-800 group-hover:text-blue-600' 
                  : 'group-hover:text-blue-400'
              }`}>{link.label}</span>
            </motion.a>
          ))}
        </div>
        
        <div className={`glass-morphism rounded-2xl p-8 mb-12 relative overflow-hidden ${
          theme === 'light' 
            ? 'border-cyan-600/30 bg-white/95' 
            : 'border-cyan-500/20'
        }`}>
          {/* Background glow effects - improved for light mode */}
          <div className={`absolute -inset-10 blur-3xl rounded-full ${
            theme === 'light' 
              ? 'bg-gradient-to-r from-blue-600/3 via-cyan-600/2 to-blue-600/3' 
              : 'bg-gradient-to-r from-blue-500/5 via-cyan-500/3 to-blue-500/5'
          }`}></div>
          
          <div className="relative grid grid-cols-1 md:grid-cols-3 gap-12">
            {/* Logo & About */}
            <div>
              <motion.a 
                href="#hero" 
                className={`text-2xl font-medium tracking-tighter hover:text-primary transition-colors duration-300 flex items-center ${
                  theme === 'light' ? 'text-gray-900' : ''
                }`}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick('hero');
                }}
                whileHover={{ scale: 1.05 }}
              >
                <span className={`bg-gradient-to-r bg-clip-text text-transparent ${
                  theme === 'light' 
                    ? 'from-blue-600 via-cyan-600 to-blue-600' 
                    : 'from-blue-400 via-cyan-500 to-blue-400'
                }`}>
                  Portfolio
                </span>
                <span className="text-primary font-bold text-3xl ml-1">.</span>
              </motion.a>
              
              <p className={`mt-4 ${
                theme === 'light' ? 'text-gray-700' : 'text-muted-foreground'
              }`}>
                {t('footer.about_me')}
              </p>
              
              <div className="mt-6 flex items-center">
                <div className="flex space-x-2">
                  <Coffee className={`h-4 w-4 ${theme === 'light' ? 'text-pink-600' : 'text-pink-400'}`} />
                  <Code className={`h-4 w-4 ${theme === 'light' ? 'text-purple-600' : 'text-purple-400'}`} />
                  <Cpu className={`h-4 w-4 ${theme === 'light' ? 'text-cyan-600' : 'text-cyan-400'}`} />
                </div>
                <span className={`ml-2 text-xs ${
                  theme === 'light' ? 'text-gray-600' : 'text-muted-foreground'
                }`}>{t('footer.coded_with_passion')}</span>
              </div>
            </div>
            
            {/* Quick Links */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 bg-gradient-to-r bg-clip-text text-transparent ${
                theme === 'light' 
                  ? 'from-blue-600 to-cyan-600' 
                  : 'from-blue-400 to-cyan-400'
              }`}>{t('footer.navigation')}</h3>
              <ul className="space-y-3 grid grid-cols-2">
                {[
                  { name: t('footer.links.home'), id: 'hero' },
                  { name: t('footer.links.about'), id: 'about' },
                  { name: t('footer.links.projects'), id: 'projects' },
                  { name: t('footer.links.testimonials'), id: 'testimonials' },
                  { name: t('footer.links.experience'), id: 'experience' },
                  { name: t('footer.links.services'), id: 'services' },
                  { name: t('footer.links.contact'), id: 'contact' }
                ].map((link) => (
                  <li key={link.id}>
                    <a
                      href={`#${link.id}`}
                      className={`transition-colors duration-300 link-hover flex items-center ${
                        theme === 'light' 
                          ? 'text-gray-700 hover:text-gray-900' 
                          : 'text-muted-foreground hover:text-foreground'
                      }`}
                      onClick={(e) => {
                        e.preventDefault();
                        handleNavClick(link.id);
                      }}
                    >
                      <span className={`w-1 h-1 rounded-full mr-2 ${
                        theme === 'light' ? 'bg-blue-600' : 'bg-blue-400'
                      }`}></span>
                      {link.name}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
            
            {/* Contact Info */}
            <div>
              <h3 className={`text-lg font-semibold mb-4 bg-gradient-to-r bg-clip-text text-transparent ${
                theme === 'light' 
                  ? 'from-cyan-600 to-blue-600' 
                  : 'from-cyan-400 to-blue-400'
              }`}>{t('footer.contact')}</h3>
              <div className="space-y-4">
                <div className={`flex items-start ${
                  theme === 'light' ? 'text-gray-700' : 'text-muted-foreground'
                }`}>
                  <Mail className={`w-5 h-5 mr-3 flex-shrink-0 mt-1 ${
                    theme === 'light' ? 'text-blue-600' : 'text-blue-400'
                  }`} />
                  <a 
                    href="mailto:elhaddad.nassimpro@gmail.com"
                    className={`transition-colors duration-300 ${
                      theme === 'light' ? 'hover:text-gray-900' : 'hover:text-foreground'
                    }`}
                  >
                    elhaddad.nassimpro@gmail.com
                  </a>
                </div>
                <div className={`flex items-start ${
                  theme === 'light' ? 'text-gray-700' : 'text-muted-foreground'
                }`}>
                  <MapPin className={`w-5 h-5 mr-3 flex-shrink-0 mt-1 ${
                    theme === 'light' ? 'text-cyan-600' : 'text-cyan-400'
                  }`} />
                  <span>{t('contact.location')}</span>
                </div>
                <motion.div 
                  className={`mt-6 glass-morphism rounded-lg p-4 transition-all duration-300 ${
                    theme === 'light' 
                      ? 'bg-gradient-to-br from-blue-600/5 to-cyan-600/3 bg-white/60' 
                      : 'bg-gradient-to-br from-blue-500/10 to-cyan-500/5'
                  }`}
                  whileHover={{ 
                    y: -5, 
                    boxShadow: theme === 'light' 
                      ? "0 10px 20px rgba(59,130,246,0.1)" 
                      : "0 10px 20px rgba(59,130,246,0.2)" 
                  }}
                >
                  <p className={`text-sm ${
                    theme === 'light' ? 'text-gray-800' : ''
                  }`}>
                    {t('footer.available_for_projects')}
                  </p>
                </motion.div>
              </div>
            </div>
          </div>
        </div>
        
        <div className={`flex flex-col md:flex-row justify-between items-center pt-6 ${
          theme === 'light' ? 'border-t border-gray-300/50' : 'border-t border-white/10'
        }`}>
          <p className={`text-sm flex items-center ${
            theme === 'light' ? 'text-gray-700' : 'text-muted-foreground'
          }`}>
            {t('footer.copyright')}
            <motion.span 
              className={`ml-2 inline-flex items-center ${
                theme === 'light' ? 'text-blue-600' : 'text-blue-400'
              }`}
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 2 }}
            >
              <Heart className="h-3 w-3 mr-1" />
            </motion.span>
          </p>
          
          <div className="mt-4 md:mt-0 flex items-center">
            <span className={`text-xs mr-4 ${
              theme === 'light' ? 'text-gray-600' : 'text-muted-foreground'
            }`}>{t('footer.easter_egg_hint')}</span>
            
            <motion.button 
              onClick={() => handleNavClick('hero')}
              className={`flex items-center px-4 py-2 text-sm glass-morphism rounded-full transition-all duration-300 ${
                theme === 'light' 
                  ? 'bg-white/70 text-gray-800 hover:bg-white/90' 
                  : 'hover:bg-white/10'
              }`}
              whileHover={{ y: -3, boxShadow: "0 10px 15px rgba(0,0,0,0.1)" }}
            >
              <span>{t('footer.top')}</span>
              <ChevronUp className="w-4 h-4 ml-2" />
            </motion.button>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
